import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { AdmissionRefusal } from '@tb/shared';
import type { Net } from '../net';
import { Dialog, Icon, Notice } from '../ui/kit';
import { rememberPassword, rememberedPassword } from './gate';

/** What a tester gate says, and the test ids its dialog and button carry. */
export interface GateCopy {
  kicker: string;
  why: string;
  note?: string;
  /** `war` gives `war-gate` and `war-unlock`. */
  testid: string;
}

/** The war, while the game is under development. */
export const WAR_GATE: GateCopy = { kicker: 'Under development', why: 'Enter the tester password to join a war.', testid: 'war' };

/** Training and battles, while the game is closed to the public from the simulation room (D36). */
export const CLOSED_GATE: GateCopy = {
  kicker: 'Closed for now',
  why: 'Trading Blows is closed to the public for now. Enter the tester password to play.',
  testid: 'access',
};

/**
 * A button that a tester gate may stand in front of. A press goes to the
 * server with the remembered password, if there is one; a refusal with
 * `code`, answering this press, opens the gate, and a word the server turned
 * down is forgotten. Only an answer to this press opens it, not a refusal
 * left over from before. `asking` is whether the gate is open, so the page
 * does not say the refusal twice.
 */
export function useTesterEntry(net: Pick<Net, 'refusal'>, code: AdmissionRefusal, copy: GateCopy, busy = false): {
  ask: (go: (password?: string) => void) => void; gate: ReactNode; asking: boolean;
} {
  const [asking, setAsking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const sent = useRef<{ password: string | null; go: (password?: string) => void } | null>(null);
  const retry = useRef<((password?: string) => void) | null>(null);
  useEffect(() => {
    const press = sent.current;
    if (!press || net.refusal?.code !== code) return;
    sent.current = null;
    if (press.password) rememberPassword(null);
    setProblem(press.password ? net.refusal.message : null);
    retry.current = press.go;
    setAsking(true);
  }, [net.refusal]);
  const ask = (go: (password?: string) => void, password = rememberedPassword()) => {
    sent.current = { password, go };
    go(password ?? undefined);
  };
  const gate = asking && <TesterGate copy={copy} problem={problem} busy={busy}
    onUnlock={(password) => { rememberPassword(password); if (retry.current) ask(retry.current, password); }}
    onClose={() => { setAsking(false); setProblem(null); }} />;
  return { ask, gate, asking };
}

/**
 * The war's button, wherever it is (the lobby, the end of training): its
 * tester gate while the game is under development.
 */
export function useWarEntry(net: Pick<Net, 'play' | 'refusal' | 'access'>, busy = false, onPress?: () => void): { enter: () => void; gate: ReactNode } {
  const copy = { ...WAR_GATE, ...(net.access.open ? { note: 'Command training is open to everyone.' } : {}) };
  const entry = useTesterEntry(net, 'WAR_PASSWORD', copy, busy);
  return { enter: () => { onPress?.(); entry.ask((password) => net.play(password)); }, gate: entry.gate };
}

/**
 * Asked when a newcomer presses a gated button: what is going on, and the
 * password that lets a tester in. The server checks the word itself
 * (simGate.ts), so this is only the asking.
 */
export function TesterGate({ copy, problem, busy, onUnlock, onClose }: {
  copy: GateCopy; problem: string | null; busy: boolean; onUnlock: (password: string) => void; onClose: () => void;
}) {
  const [guess, setGuess] = useState('');
  const id = `${copy.testid}-password`;
  return (
    <Dialog size="sm" material="housing" className="war-gate" onClose={onClose} kicker={copy.kicker} title="Tester access"
      data-testid={`${copy.testid}-gate`} aria-describedby={`${copy.testid}-gate-why`}
      aside={<button className="btn sm ghost icon" onClick={onClose} aria-label="Close"><Icon name="close" /></button>}>
      <p id={`${copy.testid}-gate-why`}>{copy.why}</p>
      {copy.note && <p className="dim">{copy.note}</p>}
      <form className="row war-gate-form" onSubmit={(e) => { e.preventDefault(); if (guess) onUnlock(guess); }}>
        <label className="sr-only" htmlFor={id}>Password</label>
        <input id={id} className="grow" type="password" autoComplete="current-password" value={guess}
          onChange={(e) => setGuess(e.target.value)} autoFocus />
        <button className="btn primary" type="submit" disabled={busy || !guess} data-testid={`${copy.testid}-unlock`}>Unlock</button>
      </form>
      {problem && <Notice tone="bad" inline role="alert">{problem}</Notice>}
    </Dialog>
  );
}
